import React, { useState, useCallback } from 'react';
import './fab.css';

export type FABAction = {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
};

export type FABProps = {
  icon?: React.ReactNode;
  expandedIcon?: React.ReactNode;
  ariaLabel: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'gradient';
  size?: 'md' | 'lg';
  pulse?: boolean;
  actions?: FABAction[];
  position?: 'bottom-right' | 'bottom-center' | 'bottom-left';
};

const FAB: React.FC<FABProps> = ({
  icon = '+',
  expandedIcon,
  ariaLabel,
  onClick,
  variant = 'gradient',
  size = 'md',
  pulse = false,
  actions,
  position = 'bottom-right',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = useCallback(() => {
    if (actions && actions.length > 0) {
      setIsExpanded(prev => !prev);
    } else if (onClick) {
      onClick();
    }
  }, [actions, onClick]);

  const handleActionClick = useCallback((action: FABAction) => {
    action.onClick();
    setIsExpanded(false);
  }, []);

  const handleBackdropClick = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const classes = [
    'fab',
    `fab--${variant}`,
    `fab--${size}`,
    `fab--${position}`,
    pulse ? 'fab--pulse' : '',
    isExpanded ? 'fab--expanded' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {actions && isExpanded && (
        <div className="fab-backdrop" onClick={handleBackdropClick} aria-hidden="true" />
      )}
      <div className={`fab-container fab-container--${position}`}>
        {actions && isExpanded && (
          <div className="fab-actions">
            {actions.map((action, index) => (
              <button
                key={index}
                className="fab-action"
                onClick={() => handleActionClick(action)}
                aria-label={action.label}
                style={{ '--action-index': index } as React.CSSProperties}
              >
                <span className="fab-action__icon">{action.icon}</span>
                <span className="fab-action__label">{action.label}</span>
              </button>
            ))}
          </div>
        )}
        <button className={classes} aria-label={ariaLabel} onClick={handleClick}>
          <span className="fab__icon" aria-hidden="true">
            {isExpanded && expandedIcon ? expandedIcon : icon}
          </span>
        </button>
      </div>
    </>
  );
};

export default FAB;


